import { clamp } from "./numeric.js";

/* ===========================================================================
   Image primitives. Every buffer is a plain typed array plus width/height; there is
   no Mat type, because nothing here needs one.
   =========================================================================== */

/**
 * cv2.cvtColor's BGR2GRAY, which on 8-bit input is fixed-point rather than the float
 * 0.299/0.587/0.114 it is usually written as. The distinction is not pedantry: the
 * hashes in the index were computed from these rounded integers, and a half-level
 * difference flips dHash bits wherever two neighbouring cells are nearly equal.
 *
 * Fifteen-bit constants, not the fourteen-bit set the formula is usually quoted with.
 * OpenCV widened these and 3735 is *not* 2 x 1868 — the blue coefficient was re-rounded
 * down, not doubled. Checked against `cv2.cvtColor` over 160,000 random pixels and the
 * full grey ramp: the pair below is exact, and the fourteen-bit pair it replaced was one
 * level out on 0.24% of them.
 */
export function grayValue(r, g, b) {
  return (r * 9798 + g * 19235 + b * 3735 + 16384) >> 15;
}

export function toGray(rgba, w, h) {
  const out = new Float32Array(w * h);
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = grayValue(rgba[p], rgba[p + 1], rgba[p + 2]);
  }
  return out;
}

/** cvRound — round half to even, which is what saturate_cast<uchar> does. */
export function cvRound(v) {
  const floor = Math.floor(v);
  const frac = v - floor;
  if (frac > 0.5) return floor + 1;
  if (frac < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** In-place uint8 quantisation of a resize result — cv2.resize's 8-bit output. */
export function toU8(values) {
  for (let i = 0; i < values.length; i++) values[i] = clamp(cvRound(values[i]), 0, 255);
  return values;
}

/** cv2.getGaussianKernel with sigma=0 — the size-derived sigma OpenCV substitutes. */
export function gaussianKernel(size) {
  const sigma = 0.3 * ((size - 1) * 0.5 - 1) + 0.8;
  const half = (size - 1) / 2;
  const k = new Float32Array(size);
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - half;
    k[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
    sum += k[i];
  }
  for (let i = 0; i < size; i++) k[i] /= sum;
  return k;
}

/** Separable Gaussian with BORDER_REFLECT_101, which is OpenCV's default. */
export function gaussianBlur(src, w, h, size) {
  const k = gaussianKernel(size),
    half = (size - 1) / 2;
  const tmp = new Float32Array(w * h),
    out = new Float32Array(w * h);
  const reflect = (i, n) => (i < 0 ? -i : i >= n ? 2 * n - i - 2 : i);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let t = 0; t < size; t++) acc += k[t] * src[y * w + reflect(x + t - half, w)];
      tmp[y * w + x] = acc;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let t = 0; t < size; t++) acc += k[t] * tmp[reflect(y + t - half, h) * w + x];
      out[y * w + x] = acc;
    }
  }
  return out;
}

/** Sobel 3x3 derivatives, replicate-padded. */
export function sobel(src, w, h) {
  const gx = new Float32Array(w * h),
    gy = new Float32Array(w * h);
  const at = (x, y) => src[clamp(y, 0, h - 1) * w + clamp(x, 0, w - 1)];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tl = at(x - 1, y - 1),
        tc = at(x, y - 1),
        tr = at(x + 1, y - 1);
      const ml = at(x - 1, y),
        mr = at(x + 1, y);
      const bl = at(x - 1, y + 1),
        bc = at(x, y + 1),
        br = at(x + 1, y + 1);
      gx[y * w + x] = tr + 2 * mr + br - (tl + 2 * ml + bl);
      gy[y * w + x] = bl + 2 * bc + br - (tl + 2 * tc + tr);
    }
  }
  return { gx, gy };
}

/**
 * Canny, matching cv2.Canny's defaults: L1 gradient magnitude, 3x3 Sobel, and
 * hysteresis between the two thresholds. The input is expected to be blurred already,
 * exactly as CardDetector.edges does it.
 */
export function canny(blurred, w, h, lo, hi) {
  const { gx, gy } = sobel(blurred, w, h);
  const mag = new Float32Array(w * h);
  for (let i = 0; i < mag.length; i++) mag[i] = Math.abs(gx[i]) + Math.abs(gy[i]);

  // Non-maximum suppression along the quantised gradient direction.
  const keep = new Uint8Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x,
        m = mag[i];
      if (m < lo) continue;
      const ax = Math.abs(gx[i]),
        ay = Math.abs(gy[i]);
      let a, b;
      if (ax > 2.414 * ay) {
        a = mag[i - 1];
        b = mag[i + 1];
      } // ~horizontal
      else if (ay > 2.414 * ax) {
        a = mag[i - w];
        b = mag[i + w];
      } // ~vertical
      else if (gx[i] > 0 === gy[i] > 0) {
        a = mag[i - w - 1];
        b = mag[i + w + 1];
      } else {
        a = mag[i - w + 1];
        b = mag[i + w - 1];
      }
      if (m >= a && m >= b) keep[i] = m >= hi ? 2 : 1;
    }
  }

  // Hysteresis: strong pixels seed, weak survivors must connect to a seed.
  const out = new Uint8Array(w * h);
  const stack = [];
  for (let i = 0; i < keep.length; i++)
    if (keep[i] === 2) {
      out[i] = 255;
      stack.push(i);
    }
  while (stack.length) {
    const i = stack.pop(),
      x = i % w,
      y = (i / w) | 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx,
          ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const j = ny * w + nx;
        if (keep[j] === 1 && !out[j]) {
          out[j] = 255;
          stack.push(j);
        }
      }
    }
  }
  return out;
}

/**
 * Separable rectangular min/max filter — erode when `min`, dilate otherwise.
 *
 * van Herk/Gil-Werman: three comparisons per pixel whatever the kernel size, where
 * the naive scan pays `size` comparisons per pixel per axis. Detection runs four of
 * these per colour mask (close 9, open 5) and the live loop's change mask adds four
 * more per frame, which is what promoted this from "fine" to the largest fixed cost
 * in a detection pass. The output is bit-identical to the naive version: taps that
 * fall outside the image are simply absent, which is the same as padding the line
 * with the operation's identity element.
 */
export function morphPass(src, w, h, size, takeMin) {
  const half = (size - 1) >> 1;
  const identity = takeMin ? 255 : 0;
  const tmp = new Uint8Array(w * h),
    out = new Uint8Array(w * h);
  const longest = Math.max(w, h) + 2 * half;
  const line = new Uint8Array(longest),
    pre = new Uint8Array(longest);
  const suf = new Uint8Array(longest),
    res = new Uint8Array(longest);

  // One padded line: res[i] = extremum(line[i .. i+size-1]), read as the meet of a
  // per-block suffix at i and a per-block prefix at i+size-1 — the window spans at
  // most two size-aligned blocks, and those two runs cover it exactly.
  const runLine = (n) => {
    const len = n + 2 * half;
    for (let j = 0, block = 0; j < len; j++, block++) {
      if (block === size) block = 0;
      const v = line[j];
      if (block === 0) pre[j] = v;
      else {
        const p = pre[j - 1];
        pre[j] = takeMin ? (v < p ? v : p) : v > p ? v : p;
      }
    }
    for (let j = len - 1, block = (len - 1) % size; j >= 0; j--, block--) {
      if (block < 0) block = size - 1;
      const v = line[j];
      if (j === len - 1 || block === size - 1) suf[j] = v;
      else {
        const s = suf[j + 1];
        suf[j] = takeMin ? (v < s ? v : s) : v > s ? v : s;
      }
    }
    for (let i = 0; i < n; i++) {
      const a = suf[i],
        b = pre[i + size - 1];
      res[i] = takeMin ? (a < b ? a : b) : a > b ? a : b;
    }
  };

  for (let y = 0; y < h; y++) {
    line.fill(identity, 0, w + 2 * half);
    const row = y * w;
    for (let x = 0; x < w; x++) line[x + half] = src[row + x];
    runLine(w);
    tmp.set(res.subarray(0, w), row);
  }
  for (let x = 0; x < w; x++) {
    line.fill(identity, 0, h + 2 * half);
    for (let y = 0; y < h; y++) line[y + half] = tmp[y * w + x];
    runLine(h);
    for (let y = 0; y < h; y++) out[y * w + x] = res[y];
  }
  return out;
}

export const morphClose = (src, w, h, k) =>
  morphPass(morphPass(src, w, h, k, false), w, h, k, true);
export const morphOpen = (src, w, h, k) => morphPass(morphPass(src, w, h, k, true), w, h, k, false);

/**
 * INTER_AREA resize — exact area-weighted averaging, which is what every hash in the
 * index was computed through. Bilinear here would shift hash bits on every card.
 * Works on interleaved channels; `src` may be Uint8ClampedArray or Float32Array.
 */
export function resizeArea(src, sw, sh, dw, dh, channels) {
  const out = new Float32Array(dw * dh * channels);
  const sx = sw / dw,
    sy = sh / dh;
  const acc = new Float64Array(channels);
  for (let dy = 0; dy < dh; dy++) {
    const y0 = dy * sy,
      y1 = (dy + 1) * sy;
    const iy0 = Math.floor(y0),
      iy1 = Math.min(sh - 1, Math.ceil(y1) - 1);
    for (let dx = 0; dx < dw; dx++) {
      const x0 = dx * sx,
        x1 = (dx + 1) * sx;
      const ix0 = Math.floor(x0),
        ix1 = Math.min(sw - 1, Math.ceil(x1) - 1);
      let weight = 0;
      // Hoisted accumulator, not a fresh Float64Array per destination pixel: on a
      // 12 MP photo that allocation ran millions of times and was pure GC churn.
      acc.fill(0, 0, channels);
      for (let y = iy0; y <= iy1; y++) {
        const wy = Math.min(y + 1, y1) - Math.max(y, y0);
        if (wy <= 0) continue;
        for (let x = ix0; x <= ix1; x++) {
          const wx = Math.min(x + 1, x1) - Math.max(x, x0);
          if (wx <= 0) continue;
          const a = wy * wx,
            base = (y * sw + x) * channels;
          for (let c = 0; c < channels; c++) acc[c] += a * src[base + c];
          weight += a;
        }
      }
      const base = (dy * dw + dx) * channels;
      for (let c = 0; c < channels; c++) out[base + c] = weight > 0 ? acc[c] / weight : 0;
    }
  }
  return out;
}

/** sRGB gamma decode, tabulated — the inner loop of every Lab conversion on the page. */
export const SRGB_LINEAR = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    t[i] = c > 0.04045 ? Math.pow((c + 0.055) / 1.055, 2.4) : c / 12.92;
  }
  return t;
})();

/**
 * RGBA canvas bytes to OpenCV's 8-bit CIELAB scaling (L*255/100, a+128, b+128),
 * interleaved. The 0-255 range matters: DetectionSettings.background_distance and the
 * indexed colour signature are both expressed in these units.
 */
export function rgbaToLab(rgba, w, h) {
  const out = new Float32Array(w * h * 3);
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  for (let i = 0, p = 0, o = 0; i < w * h; i++, p += 4, o += 3) {
    const r = SRGB_LINEAR[rgba[p]],
      g = SRGB_LINEAR[rgba[p + 1]],
      b = SRGB_LINEAR[rgba[p + 2]];
    const X = (0.412453 * r + 0.35758 * g + 0.180423 * b) / 0.950456;
    const Y = 0.212671 * r + 0.71516 * g + 0.072169 * b;
    const Z = (0.019334 * r + 0.119193 * g + 0.950227 * b) / 1.088754;
    const fx = f(X),
      fy = f(Y),
      fz = f(Z);
    const L = Y > 0.008856 ? 116 * fy - 16 : 903.3 * Y;
    // Rounded, because cvtColor's 8-bit path is what both the colour signature and
    // DetectionSettings.background_distance were measured against.
    out[o] = clamp(cvRound((L * 255) / 100), 0, 255);
    out[o + 1] = clamp(cvRound(500 * (fx - fy) + 128), 0, 255);
    out[o + 2] = clamp(cvRound(200 * (fy - fz) + 128), 0, 255);
  }
  return out;
}

/**
 * Variance of the 3x3 Laplacian — `cv2.Laplacian(gray, CV_32F).var()`.
 *
 * The sharpness statistic the scanner ranks a track's frames by. A card moving through
 * a hand is never still, so the motion settle has no *stillest* frame to pick; it picks
 * the sharpest instead, and this is the number that decides which one that is. Only the
 * ordering matters, so the border handling (replicate, as OpenCV does) is the only part
 * that has to be right rather than the absolute scale.
 */
export function laplacianVariance(rgba, w, h) {
  const gray = toGray(rgba, w, h);
  const at = (x, y) => gray[clamp(y, 0, h - 1) * w + clamp(x, 0, w - 1)];
  let sum = 0,
    sumSquares = 0;
  const n = w * h;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const value = at(x, y - 1) + at(x - 1, y) - 4 * at(x, y) + at(x + 1, y) + at(x, y + 1);
      sum += value;
      sumSquares += value * value;
    }
  }
  const mean = sum / n;
  return sumSquares / n - mean * mean;
}

/**
 * Gaussian blur over interleaved RGBA, returning rounded 8-bit channels.
 *
 * The colour masks are built from `cv2.cvtColor(cv2.GaussianBlur(frame, (5, 5), 0),
 * COLOR_BGR2LAB)`, and the page was converting the *raw* frame instead — so background
 * segmentation and the change mask both ran on per-pixel Lab distances that carried all
 * of the sensor noise the blur exists to remove, against a `background_distance` tuned
 * with it removed.
 */
export function gaussianBlurRgba(rgba, w, h, size) {
  const out = new Uint8ClampedArray(w * h * 4);
  const plane = new Float32Array(w * h);
  for (let c = 0; c < 3; c++) {
    for (let i = 0; i < w * h; i++) plane[i] = rgba[i * 4 + c];
    const blurred = gaussianBlur(plane, w, h, size);
    for (let i = 0; i < w * h; i++) out[i * 4 + c] = clamp(cvRound(blurred[i]), 0, 255);
  }
  for (let i = 0; i < w * h; i++) out[i * 4 + 3] = 255;
  return out;
}
